import { ImagesType } from "@features/images/type";
import * as Accordion from "@radix-ui/react-accordion";
import { ChevronDownIcon } from "@radix-ui/react-icons";
import { Heading } from "@radix-ui/themes";
import { AnimatePresence, motion } from "framer-motion";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { StudioAIStyles } from "./effects/ai-styles";
import {
  getAdjustmentCategories,
  AdjustmentSettings,
  defaultSettings,
} from "./effects/cursors";
import { AdjustmentCategory } from "./effects/cursors-category";
import { FilterCategory } from "./effects/filter-category";
import { getFilterCategories } from "./effects/filters";
import { useStudio } from "@features/studio/use-studio";

interface StudioPhotoAccordionProps {
  image: ImagesType;
  onAction: (action: string) => void;
}

export const StudioPhotoAccordion = ({
  image,
  onAction,
}: StudioPhotoAccordionProps) => {
  const { t } = useTranslation();
  const { studio, setStudio } = useStudio();
  const [tab, setTab] = useState("vintage");

  const filter = studio.effects.filter;
  const cursorSettings = studio.effects.adjustements || {};
  const adjustmentCategories = getAdjustmentCategories(t);
  const filterCategories = getFilterCategories(t);

  const setFilter = (newFilter: string | null) => {
    setStudio({ effects: { filter: newFilter, adjustements: cursorSettings } });
  };

  const setCursorSettings = (newSettings: Partial<AdjustmentSettings>) => {
    setStudio({
      effects: { filter, adjustements: { ...cursorSettings, ...newSettings } },
    });
  };

  return (
    <>
      <Heading size="3">{t("studio.effects.title")}</Heading>

      <Accordion.Root
        className="-mx-4"
        type="single"
        collapsible
        defaultValue={tab}
        onValueChange={(value) => setTab(value)}
      >
        <Heading size="1" className="px-4 opacity-50">
          {t("studio.effects.restyle")}
        </Heading>

        {/* AI Styles Section */}
        <div className="overflow-hidden p-4 pb-2">
          <StudioAIStyles image={image} onAction={onAction} />
        </div>

        <div className="space-y-4 md:block hidden">
          <Heading size="1" className="px-4 pt-6 opacity-50">
            {t("studio.effects.photo_effects")}
          </Heading>

          {/* Filter Categories */}
          {Object.entries(filterCategories).map(([categoryKey, category]) => {
            const categoryHasActiveFilter = category.filters.includes(
              filter || ""
            );
            return (
              <Accordion.Item key={categoryKey} value={categoryKey}>
                <Accordion.Header className="flex">
                  <Accordion.Trigger className="group flex pt-4 px-4 flex-1 cursor-pointer items-center justify-between  leading-none outline-none hover:opacity-80">
                    <div className="flex items-center gap-2">
                      <Heading size="2">{category.name}</Heading>
                      {categoryHasActiveFilter && (
                        <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      )}
                    </div>
                    <ChevronDownIcon
                      className="ease-[cubic-bezier(0.87,_0,_0.13,_1)] transition-transform duration-300 group-data-[state=open]:rotate-180"
                      aria-hidden
                    />
                  </Accordion.Trigger>
                </Accordion.Header>
                <AnimatePresence>
                  {tab === categoryKey && (
                    <motion.div
                      layout
                      transition={{ duration: 0.3 }}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <div className="overflow-hidden p-4 pb-2">
                        <FilterCategory
                          image={image}
                          setFilter={setFilter}
                          filter={filter}
                          categoryFilters={category.filters}
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </Accordion.Item>
            );
          })}

          <Heading size="1" className="px-4 pt-6 opacity-50">
            {t("studio.effects.cursors")}
          </Heading>

          {/* Adjustment Categories */}
          {Object.entries(adjustmentCategories).map(
            ([categoryKey, category]) => {
              const categoryIsModified = category.adjustments.some((adj) => {
                const currentValue = cursorSettings[adj.key];
                const defaultValue = defaultSettings[adj.key];
                return (
                  JSON.stringify(currentValue) !== JSON.stringify(defaultValue)
                );
              });
              return (
                <Accordion.Item key={categoryKey} value={categoryKey}>
                  <Accordion.Header className="flex">
                    <Accordion.Trigger className="group flex pt-4 px-4 flex-1 cursor-pointer items-center justify-between  leading-none outline-none hover:opacity-80">
                      <div className="flex items-center gap-2">
                        <Heading size="2">{category.name}</Heading>
                        {categoryIsModified && (
                          <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                        )}
                      </div>
                      <ChevronDownIcon
                        className="ease-[cubic-bezier(0.87,_0,_0.13,_1)] transition-transform duration-300 group-data-[state=open]:rotate-180"
                        aria-hidden
                      />
                    </Accordion.Trigger>
                  </Accordion.Header>
                  <AnimatePresence>
                    {tab === categoryKey && (
                      <motion.div
                        layout
                        transition={{ duration: 0.3 }}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: "auto" }}
                        exit={{ opacity: 0, height: 0 }}
                      >
                        <div className="overflow-hidden p-4 pb-2">
                          <AdjustmentCategory
                            settings={cursorSettings}
                            onSettingsChange={setCursorSettings}
                            categoryAdjustments={category.adjustments}
                          />
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Accordion.Item>
              );
            }
          )}
        </div>
      </Accordion.Root>
    </>
  );
};

interface StudioPhotoEffectsProps {
  image: any;
  onAction: (action: string) => void;
}

export const StudioPhotoEffects = ({
  image,
  onAction,
}: StudioPhotoEffectsProps) => {
  return <StudioPhotoAccordion image={image} onAction={onAction} />;
};
